import { useRef } from 'react';
import { Inline, Radio, RadioGroup, Stack, Text, ValidationMessage, textVariants } from '@etg/wings';
import { useProperty, useTranslation, useValidation } from '@eti/providers';
import { constants, price as priceUtils } from '@eti/utils';
import { css, mediaQueries } from '@eti/styles';
import Media from 'react-media';
import { Field, FormSection } from 'redux-form';
import { screenReaderOnly } from '../../../common/utils/styles';
import { isCarrierCodesPresentInAllSegments } from '../../../common/utils/carriersUtils';
import useBaggageTracker from '../hooks/useBaggageTracker';
import { usePersuasionModal } from '../../../common/context/PersuasionModalContext';
import { isEnabled } from '../../../common/utils/modal';
import ProductModal from '../../../common/components/ProductModal';
import { getPricePerBound } from '../utils/priceUtil';
import BaggagePriceBreakdownTooltip from './BaggagePriceBreakdownTooltip';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const productDescriptionStyle = css`
  flex-grow: 1;

  @media ${mediaQueries.small.only} {
    line-height: 1;
  }
`;
const errorStyles = css`
  margin-inline-start: 0;
`;
const BaggageSelectionRadioGroup = _ref => {
  var _order$bounds$length, _order$bounds, _order$configuration;
  let {
    baggageOptions,
    input,
    meta,
    onChange,
    order,
    persuasionContent,
    persuasionTitle,
    productId,
    productName,
    productPosition,
    radioGroupScreenReaderDescription,
    rootElement,
    shouldApplySelectionToAll,
    travelerId,
    secondBagProductId,
    totalBaggageQuantity
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    p
  } = useProperty();
  const isBaggagePriceBreakdownTooltipEnabled = p('IbeClient.Baggages.Card.Price.Tooltip.Enabled');
  const ryanairCarrierCodes = p('IbeClient.RyanairMarketingCarriers') || [];
  const isOnlyRyanairInTrip = isCarrierCodesPresentInAllSegments(ryanairCarrierCodes, order.bounds);
  const hasError = Boolean((meta === null || meta === void 0 ? void 0 : meta.touched) && (meta === null || meta === void 0 ? void 0 : meta.error));
  const uniqueId = `${productName}-traveler-${travelerId}`;
  const perBoundLabel = t('Products.Price.Label.PerBounds', '');
  const numberOfBounds = (_order$bounds$length = (_order$bounds = order.bounds) === null || _order$bounds === void 0 ? void 0 : _order$bounds.length) !== null && _order$bounds$length !== void 0 ? _order$bounds$length : 0;
  const showPricePerBound = Boolean((order === null || order === void 0 || (_order$configuration = order.configuration) === null || _order$configuration === void 0 || (_order$configuration = _order$configuration.travelerDetails) === null || _order$configuration === void 0 ? void 0 : _order$configuration.showPricePerBound) && numberOfBounds > 1 && perBoundLabel);
  const selectedTraveler = order.travelers[Number(travelerId)];
  const yesOptionRef = useRef(null);
  const {
    isOpen,
    onConfirm,
    onDecline,
    setIsOpen,
    setOnConfirm,
    setOnDecline
  } = usePersuasionModal();
  const travelersWithoutInfants = order.travelers.filter(_ref2 => {
    let {
      ageType
    } = _ref2;
    return ageType !== constants.ageTypes.INFANT;
  });
  const {
    addToCartEventOnChange,
    addToCartModalEvent
  } = useBaggageTracker(productName, travelersWithoutInfants.length, totalBaggageQuantity, travelerId, productPosition, shouldApplySelectionToAll, rootElement);
  const handleChange = (selectedTravelerId, selectedValue) => {
    var _order$persuasion;
    if (!selectedValue) {
      return;
    }
    const showPersuasion = Number(selectedTravelerId) === 0 && (order === null || order === void 0 || (_order$persuasion = order.persuasion) === null || _order$persuasion === void 0 ? void 0 : _order$persuasion.isEnabled);
    if (showPersuasion && selectedValue === 'false') {
      onChange('false', selectedTravelerId, shouldApplySelectionToAll);
      setIsOpen(true);
      setOnConfirm(() => () => {
        setIsOpen(false);
        setTimeout(() => {
          var _yesOptionRef$current;
          onChange('true', selectedTravelerId, shouldApplySelectionToAll);
          yesOptionRef === null || yesOptionRef === void 0 || (_yesOptionRef$current = yesOptionRef.current) === null || _yesOptionRef$current === void 0 || _yesOptionRef$current.focus();
        }, 530);
      });
      setOnDecline(() => () => {
        onChange('false', selectedTravelerId, shouldApplySelectionToAll);
        setIsOpen(false);
      });
    } else {
      onChange(selectedValue, selectedTravelerId, shouldApplySelectionToAll);
    }
    addToCartEventOnChange(selectedValue);
  };
  const handleOnConfirm = () => {
    onConfirm();
    addToCartModalEvent(true);
  };
  const handleOnDecline = () => {
    onDecline();
    addToCartModalEvent(false);
  };
  const renderBaggageInformation = _ref3 => {
    let {
      id,
      label,
      markup,
      priceRaw
    } = _ref3;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Inline, {
        className: productDescriptionStyle,
        children: /*#__PURE__*/_jsx("bdi", {
          children: label
        })
      }), /*#__PURE__*/_jsxs(Inline, {
        spacing: 4,
        children: [isBaggagePriceBreakdownTooltipEnabled && isOnlyRyanairInTrip && Boolean(priceRaw) && /*#__PURE__*/_jsx(BaggagePriceBreakdownTooltip, {
          "data-testid": uniqueId,
          markup: markup,
          numberOfBounds: numberOfBounds,
          priceRaw: priceRaw,
          productId: productId,
          showPricePerBound: showPricePerBound
        }), Boolean(priceRaw) && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            "data-testid": `toggleChoice-${productName}-${id}-price`,
            variant: textVariants._14_BOLD,
            children: priceUtils.formatPrice(getPricePerBound({
              numberOfBounds,
              priceRaw,
              showPricePerBound
            }))
          }), showPricePerBound && /*#__PURE__*/_jsx(Text, {
            "data-testid": "per-bound-label",
            children: perBoundLabel
          })]
        })]
      })]
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: rootElement,
    children: [isEnabled(order === null || order === void 0 ? void 0 : order.persuasion) && /*#__PURE__*/_jsx(ProductModal, {
      content: persuasionContent,
      isOpen: isOpen,
      onConfirm: handleOnConfirm,
      onDecline: handleOnDecline,
      productName: productName,
      title: persuasionTitle
    }), /*#__PURE__*/_jsxs(Stack, {
      spacing: 8,
      children: [hasError && /*#__PURE__*/_jsx(ValidationMessage, {
        className: errorStyles,
        "data-testid": "purchasableBaggage-select-option-error-message",
        variant: "error",
        children: t('Product.SelectOneValidation.Message')
      }), /*#__PURE__*/_jsxs(RadioGroup, {
        "data-testid": `${productName}-radiogroup-${travelerId}`,
        hasError: hasError,
        name: uniqueId,
        onChange: event => handleChange(String(selectedTraveler.id), event === null || event === void 0 ? void 0 : event.target.value),
        selectedValue: input === null || input === void 0 ? void 0 : input.value,
        children: [radioGroupScreenReaderDescription && /*#__PURE__*/_jsx(RadioGroup.Legend, {
          className: screenReaderOnly,
          children: radioGroupScreenReaderDescription
        }), /*#__PURE__*/_jsx(Stack, {
          "data-offer-rate": order.id,
          spacing: 8,
          children: baggageOptions === null || baggageOptions === void 0 ? void 0 : baggageOptions.map(option => {
            const {
              id,
              label,
              value,
              markup = 0,
              priceRaw = 0
            } = option;
            return /*#__PURE__*/_jsx(Radio.Label, {
              "data-offer-rate": secondBagProductId,
              "data-testid": `toggleChoice-${productName}-${id}`,
              htmlFor: `custom-option-${productName}-${id}`,
              children: /*#__PURE__*/_jsxs(Inline, {
                spacing: 8,
                children: [/*#__PURE__*/_jsx(Radio, {
                  id: `custom-option-${productName}-${id}`,
                  ref: element => {
                    if (value === 'true') {
                      yesOptionRef.current = element;
                    }
                  },
                  value: value
                }), /*#__PURE__*/_jsx(Media, {
                  query: mediaQueries.small.only,
                  children: matches => matches ? /*#__PURE__*/_jsx(Stack, {
                    children: renderBaggageInformation({
                      id,
                      label,
                      markup,
                      priceRaw
                    })
                  }) : renderBaggageInformation({
                    id,
                    label,
                    markup,
                    priceRaw
                  })
                })]
              })
            }, `custom-option-${productName}-${id}`);
          })
        })]
      })]
    })]
  });
};
const BaggageSelection = _ref4 => {
  let {
    baggageOptions,
    onChange,
    order,
    persuasionContent,
    persuasionTitle,
    productId,
    productName,
    radioGroupScreenReaderDescription,
    rootElement,
    productPosition,
    secondBagProductId,
    shouldApplySelectionToAll,
    travelerId,
    totalBaggageQuantity
  } = _ref4;
  const validate = useValidation(productName);
  const name = `${productName}-traveler-${travelerId}`;
  return /*#__PURE__*/_jsx(FormSection, {
    name: productName,
    children: /*#__PURE__*/_jsx(Field, {
      component: BaggageSelectionRadioGroup,
      format: reduxValue => (reduxValue === null || reduxValue === void 0 ? void 0 : reduxValue.value) || undefined,
      name: name,
      props: {
        baggageOptions,
        onChange,
        order,
        persuasionContent,
        persuasionTitle,
        productId,
        productName,
        productPosition,
        radioGroupScreenReaderDescription,
        rootElement,
        travelerId,
        secondBagProductId,
        shouldApplySelectionToAll,
        totalBaggageQuantity
      },
      validate: validate
    })
  });
};
export default BaggageSelection;
//# sourceMappingURL=BaggageSelection.js.map